// "Reply mentions Texas — set state?" (A4, OVERNIGHT-MORE-2026-10-03).
//
// A lead with no state is held to the strictest send window and asked "Which state are you in?". When the
// candidate's reply SAYS where they are, the CRM offers that state for one click -- through the same Set state write
// (lib/leadState.js). NEVER automatic, and NEVER from a job mention: "jobs in Texas", "licensed in Texas", "moving to
// Texas" are not where they live. Only a first-person "I'm in / I live in / I'm based in / my home is in <State>", or a
// bare state sent right after our state question, counts.
import { STATE_CODES } from './leadState';

export const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut',
  DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah',
  VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};
export const STATE_QUESTION = 'Which state are you licensed in?';   // pt-agents second_touch.STATE_QUESTION (FIX-AGENTS-2026-10-12 §2)
// What counts as having asked: any "which state ...?" question (pt-agents STATE_QUESTION_RE) -- the old wording, the new
// one, and the live agent's own state question all count.
export const STATE_QUESTION_RE = /\bwhich\s+state\b[^.!?]*\?/i;

const NAME_ALT = Object.values(STATE_NAMES).sort((a, b) => b.length - a.length).map((n) => n.replace(/\s+/g, '\\s+')).join('|');
// A state: a full name (any case), or a two-letter code in capitals only ("in", "or", "me", "ok" are words).
const STATE = `(${NAME_ALT}|(?-i:[A-Z]{2}))`;
// First person, WHERE THEY ARE -- the verb is about location, never about work, licences or plans.
const FIRST_PERSON = [
  `\\bI\\s+(?:currently\\s+|now\\s+)?(?:live|reside|stay)\\s+in\\s+${STATE}\\b`,
  `\\b(?:I'?m|I\\s+am|we'?re|we\\s+are)\\s+(?:currently\\s+|now\\s+|still\\s+)?(?:based\\s+|located\\s+|living\\s+|staying\\s+)?(?:in|out\\s+of)\\s+${STATE}\\b`,
  `\\b(?:my|our)\\s+(?:home|house|tax\\s+home|address|permanent\\s+address)\\s+is\\s+in\\s+${STATE}\\b`,
];
// Two-letter codes need capitals; everything else is case-insensitive. JS has no inline flags, so codes are checked
// after the match.
const toCode = (raw) => {
  const s = String(raw || '').replace(/\s+/g, ' ').trim();
  if (/^[A-Z]{2}$/.test(s)) return STATE_CODES.includes(s) ? s : null;
  const hit = Object.entries(STATE_NAMES).find(([, n]) => n.toLowerCase() === s.toLowerCase());
  return hit ? hit[0] : null;
};
const firstPersonRe = FIRST_PERSON.map((p) => new RegExp(p.replace('(?-i:[A-Z]{2})', '[A-Za-z]{2}'), 'i'));

/** The state a single inbound message says the candidate is in, or null. `askedState`: the message answers our question. */
export function stateFromMessage(text, { askedState = false } = {}) {
  const t = String(text || '');
  for (const re of firstPersonRe) {
    const m = t.match(re);
    if (m) {
      const raw = m[1];
      // a two-letter match counts only in capitals
      const code = /^[A-Za-z]{2}$/.test(raw) ? (/^[A-Z]{2}$/.test(raw) ? toCode(raw) : null) : toCode(raw);
      if (code) return code;
    }
  }
  if (askedState) {
    // a bare answer to "Which state are you in?": "Texas", "TX.", "in Ohio", "Ohio!"
    const m = t.trim().match(new RegExp(`^(?:in\\s+|I'?m\\s+in\\s+)?(${NAME_ALT}|[A-Z]{2})\\s*[.!]?$`, 'i'));
    if (m) {
      const raw = m[1];
      return /^[A-Za-z]{2}$/.test(raw) ? (/^[A-Z]{2}$/.test(raw) ? toCode(raw) : null) : toCode(raw);
    }
  }
  return null;
}

/**
 * The newest state the candidate's own messages name, or null. `messages`: a thread, oldest first, rows with
 * { role, body } (role 'candidate' is theirs; 'agent'/'human' are ours).
 */
export function suggestStateFromThread(messages) {
  const rows = Array.isArray(messages) ? messages : [];
  for (let i = rows.length - 1; i >= 0; i--) {
    const m = rows[i];
    if (!m || m.role !== 'candidate') continue;
    let askedState = false;
    for (let j = i - 1; j >= 0; j--) {
      if (rows[j] && rows[j].role !== 'candidate') { askedState = STATE_QUESTION_RE.test(String(rows[j].body || '')); break; }
    }
    const code = stateFromMessage(m.body, { askedState });
    if (code) return code;
  }
  return null;
}
